<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

// // Store management imports
import { useBreakStore } from '@/stores/break';

// // Utils imports
import { clampIndex } from '@/utils/arrayNavigation';
import { preloadImageUrls, preloadFontFamilies } from '@/utils/waitForAssets';

// // Composables
import { useAssetLoading } from '@/composables/useAssetLoading';
import { useNacxMetrics } from '@/composables/useNacxMetrics';
import { useAutoDismissCountdown } from '@/composables/useAutoDismissCountdown';

// // Component imports
import TvP08ContentTileComp from '@/components/TvPlus08/TvP08ContentTile/TvP08ContentTileComp.vue';
import TvP08ContentRowComp from '@/components/TvPlus08/TvP08ContentRow/TvP08ContentRowComp.vue';
import TvP08FunctionButtonComp from '@/components/TvPlus08/TvP08FunctionButton/TvP08FunctionButtonComp.vue';
import TvP08PopupComp from '@/components/TvPlus08/TvP08Popup/TvP08PopupComp.vue';
import TvP08ProgressCircleComp from '@/components/TvPlus08/TvP08ProgressCircle/TvP08ProgressCircleComp.vue';

// // Data imports
import {
  pickSellerAndOfferingsOfferings,
  pickSellerAndOfferingsPsp,
  pickSellerAndOfferingsFees,
} from './pickSellerAndOfferingsOfferings';

const storeBreak = useBreakStore();

// // Content Row layout — matches the 430px tile / 16px gap convention used across this
// // Template's design. TvP08ContentRowComp measures its own rendered width to clamp scrolling,
// // so no "how many tiles fit" count is needed here.
const ROW_ITEM_WIDTH = 430;
const ROW_ITEM_GAP = 16;

// // The TVP08 weights this Template uses across all 5 stages (must stay in sync with
// // `--ft-oneUi400/500/600/700` in `_variables.scss`). Preloaded as a fixed set on every stage
// // transition rather than tracked per-stage — a font a later stage introduces for the first time
// // (e.g. Stage 02's PSP line, the only place `oneUi400` appears) otherwise never gets waited for,
// // since `useAssetLoading()`'s font-loading only scans elements already on screen at mount time.
const TVP08_FONT_FAMILIES = [
  'SamsungOneUISansGUI400',
  'SamsungOneUISansGUI500',
  'SamsungOneUISansGUI600',
  'SamsungOneUISansGUI700',
];

// // Hardcoded per the finalized flow spec — no real rating/price source exists.
const HARDCODED_SELLER_RATING = '★ 4.9';
const HARDCODED_SELLER_PRICE = '$$';

// // Asset loading
const { isAssetsLoaded, isTemplateReadyToFadeIn, waitForComponentAssets, templateFadeInHandler } =
  useAssetLoading(5000);
const containerRef = ref();

// // NACX Metrics
// Which arrow/enter keys each stage's own handler consumes below — only those should
// be withheld from TV Plus. Stages 1/2/4 never use Up/Down, so those must stay out of
// their list or they'd be silently dropped with no handler consuming them either.
// Escape/Backspace (dismiss) aren't in any list, so they always still forward.
const STAGE_CONSUMED_KEYS = {
  1: ['ArrowLeft', 'ArrowRight', 'Enter'],
  2: ['ArrowLeft', 'ArrowRight', 'Enter'],
  3: ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'],
  4: ['ArrowLeft', 'ArrowRight', 'Enter'],
  5: ['Enter'],
  error: ['Enter'],
};
const { markDismissed, trackKeyPress } = useNacxMetrics({
  suppressForwardingKeys: () => STAGE_CONSUMED_KEYS[currentStage.value] ?? [],
});

// // Per-stage idle-countdown durations (seconds), per the finalized flow spec. "error" isn't a
// // numbered stage — it's the fallback shown if the Stage 02 recommendations fetch fails/times out.
const STAGE_COUNTDOWN_SECONDS = {
  1: 15,
  2: 20,
  3: 20,
  4: 15,
  5: 10,
  error: 10,
};

const METERS_PER_MILE = 1609.344;

// // PickSellerAndOfferings setup functions

const currentStage = ref(1);
const isDismissed = ref(false);

// // Stage 01 (CTA) & Stage 04 (Checkout) & Stage 05 (Confirmation) share a 2-button (or
// // 1-button, for Stage 05) focus model: 0 = primary/leftmost, 1 = secondary/rightmost.
const focusedButtonIndex = ref(0);

// // Stage 02 (Recommended Sellers): index into storeBreak.breakAiRecommendations.
// // Stage 03 (Seller Offerings): index into the 7 hardcoded Offerings — the Function Button is
// // a separate element (see isFunctionButtonFocused below), not part of this row.
const focusedTileIndex = ref(0);

// // Stage 03 only: whether focus is on the Function Button (above the row) instead of the
// // Content Tile row. Navigated with Up/Down; focusedTileIndex is left untouched while toggling
// // this, so Down always returns focus to the last-focused tile, per the flow spec.
const isFunctionButtonFocused = ref(false);

const selectedSeller = ref(null);
const cartOfferingUuids = ref(new Set());

// // Whether Stage 01's "View Restaurants" press is waiting on the recommendations fetch (see
// // handleViewRestaurantsPress below) — shows a loading spinner in the CTA popup while true.
const isWaitingForRecommendations = ref(false);

const { secondsRemaining: countdownSecondsRemaining, start: startCountdown } =
  useAutoDismissCountdown(() => {
    dismissOverlay();
  });

// // Start Stage 01's countdown synchronously (before first paint), not inside onMounted — the
// // async asset-loading wait in onMounted would otherwise leave secondsRemaining at its default
// // 0 for a moment, visibly animating the Progress Fill in from empty instead of starting full.
startCountdown(STAGE_COUNTDOWN_SECONDS[currentStage.value]);

// // PickSellerAndOfferings handler functions

const metersToMilesRounded = (distanceMeters) => {
  if (typeof distanceMeters !== 'number') return null;
  return (distanceMeters / METERS_PER_MILE).toFixed(1);
};

const cartOfferings = computed(() =>
  pickSellerAndOfferingsOfferings.filter((offering) =>
    cartOfferingUuids.value.has(offering.offeringUuid),
  ),
);

const cartSubtotal = computed(() =>
  cartOfferings.value.reduce(
    (sum, offering) => sum + parseFloat(offering.offeringPrice.slice(1)),
    0,
  ),
);

const cartTotal = computed(
  () =>
    cartSubtotal.value +
    pickSellerAndOfferingsFees.taxAmount +
    pickSellerAndOfferingsFees.deliveryFeeAmount +
    pickSellerAndOfferingsFees.serviceFeeAmount,
);

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const sellerRowHeading = computed(() =>
  [
    capitalize(storeBreak.breakAiCriteria),
    capitalize(storeBreak.breakAiTerm),
    capitalize(storeBreak.breakAiType),
  ]
    .filter(Boolean)
    .join(' '),
);

const sellerTiles = computed(() =>
  (storeBreak.breakAiRecommendations || []).map((recommendation) => ({
    key: recommendation.uuid,
    tileImage: recommendation.imageUrl,
    tileTitle: recommendation.name,
    tileSubtitle: `${HARDCODED_SELLER_RATING} · ${metersToMilesRounded(recommendation.distanceMeters) ?? '—'} mi · ${HARDCODED_SELLER_PRICE}`,
  })),
);

const offeringTiles = computed(() =>
  pickSellerAndOfferingsOfferings.map((offering) => ({
    key: offering.offeringUuid,
    tileImage: offering.offeringImage,
    tileTitle: offering.offeringTitle,
    tileSubtitle: offering.offeringPrice,
    hasBadge: cartOfferingUuids.value.has(offering.offeringUuid),
  })),
);

const toggleOfferingInCart = (offeringUuid) => {
  const nextCart = new Set(cartOfferingUuids.value);
  if (nextCart.has(offeringUuid)) {
    nextCart.delete(offeringUuid);
  } else {
    nextCart.add(offeringUuid);
  }
  cartOfferingUuids.value = nextCart;
};

// // No real ETA source for this PoC — flat hardcoded placeholder, matching the Figma mockup.
const HARDCODED_ETA_MINUTES = 23;

const checkoutMessage = computed(() => {
  const sellerName = selectedSeller.value?.name ?? '';
  return `${sellerName} · Total $${cartTotal.value.toFixed(2)} · Arriving in ~${HARDCODED_ETA_MINUTES} min`;
});

const confirmationMessage = computed(() => {
  const sellerName = selectedSeller.value?.name ?? '';
  return `${sellerName} · Arriving in ~${HARDCODED_ETA_MINUTES} min · Charged to your saved ${pickSellerAndOfferingsPsp.pspTitle} account`;
});

// // Prefixes the generic error message with the specific failure mode (an HTTP status like
// // "403:", or "CORS:" for a network-level fetch rejection) when one is known, so this is
// // diagnosable from the screen alone rather than only from console logs.
const errorMessage = computed(() => {
  const errorCode = storeBreak.breakAiRecommendationsErrorCode;
  const baseMessage = 'Sorry, there was a problem retrieving the data.';
  return errorCode ? `${errorCode}: ${baseMessage}` : baseMessage;
});

const dismissOverlay = () => {
  if (isDismissed.value) return;
  markDismissed();
  isDismissed.value = true;
};

// // Stage-specific images to preload before revealing that stage — keeps the current stage
// // visible while the next one's images finish loading in the background, instead of switching
// // immediately and letting them pop in one by one once already on screen.
const STAGE_PRELOAD_MAX_WAIT_MS = 5000;

const getStagePreloadImageUrls = (stageNumber) => {
  if (stageNumber === 2) {
    return [...sellerTiles.value.map((tile) => tile.tileImage), pickSellerAndOfferingsPsp.pspLogo];
  }
  if (stageNumber === 3) {
    return offeringTiles.value.map((tile) => tile.tileImage);
  }
  return [];
};

const goToStage = async (stageNumber) => {
  await Promise.all([
    preloadImageUrls(getStagePreloadImageUrls(stageNumber), STAGE_PRELOAD_MAX_WAIT_MS),
    preloadFontFamilies(TVP08_FONT_FAMILIES, STAGE_PRELOAD_MAX_WAIT_MS),
  ]);
  currentStage.value = stageNumber;
};

// // Stage 02's tile row needs storeBreak.breakAiRecommendations, fetched by App.vue as a genuine
// // network round-trip that isn't guaranteed to have settled by the time the user reaches this
// // button — resolves once breakAiRecommendationsStatus leaves 'pending'/'idle', or after
// // RECOMMENDATIONS_WAIT_MAX_MS elapses, whichever comes first.
const RECOMMENDATIONS_WAIT_MAX_MS = 30000;

const waitForRecommendationsSettled = () => {
  const status = storeBreak.breakAiRecommendationsStatus;
  if (status === 'success' || status === 'error') {
    return Promise.resolve(status);
  }

  return new Promise((resolve) => {
    let isSettled = false;

    const stopWatching = watch(
      () => storeBreak.breakAiRecommendationsStatus,
      (newStatus) => {
        if (isSettled || (newStatus !== 'success' && newStatus !== 'error')) return;
        isSettled = true;
        stopWatching();
        resolve(newStatus);
      },
    );

    setTimeout(() => {
      if (isSettled) return;
      isSettled = true;
      stopWatching();
      resolve('timeout');
    }, RECOMMENDATIONS_WAIT_MAX_MS);
  });
};

const handleViewRestaurantsPress = async () => {
  if (isWaitingForRecommendations.value) return;

  if (storeBreak.breakAiRecommendationsStatus === 'success') {
    goToStage(2);
    return;
  }

  if (storeBreak.breakAiRecommendationsStatus === 'error') {
    goToStage('error');
    return;
  }

  isWaitingForRecommendations.value = true;
  const result = await waitForRecommendationsSettled();
  isWaitingForRecommendations.value = false;

  // // The user may have dismissed the overlay while this was pending — don't advance a stage on
  // // an already-dismissed overlay.
  if (isDismissed.value) return;

  if (result === 'success') {
    goToStage(2);
  } else {
    goToStage('error');
  }
};

const handleStage01Navigation = (key) => {
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    focusedButtonIndex.value = clampIndex(
      focusedButtonIndex.value,
      key === 'ArrowRight' ? 1 : -1,
      2,
    );
  } else if (key === 'Enter') {
    if (focusedButtonIndex.value === 0) {
      handleViewRestaurantsPress();
    } else {
      dismissOverlay();
    }
  }
};

const handleStage02Navigation = (key) => {
  const recommendations = storeBreak.breakAiRecommendations || [];
  if (!recommendations.length) return;

  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    focusedTileIndex.value = clampIndex(
      focusedTileIndex.value,
      key === 'ArrowRight' ? 1 : -1,
      recommendations.length,
    );
  } else if (key === 'Enter') {
    selectedSeller.value = recommendations[focusedTileIndex.value];
    focusedTileIndex.value = 0;
    goToStage(3);
  }
};

const handleStage03Navigation = (key) => {
  if (isFunctionButtonFocused.value) {
    // // Only one element up here — Left/Right have nothing to move to, so they're a no-op.
    if (key === 'ArrowDown') {
      isFunctionButtonFocused.value = false;
    } else if (key === 'Enter') {
      goToStage(4);
    }
    return;
  }

  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    focusedTileIndex.value = clampIndex(
      focusedTileIndex.value,
      key === 'ArrowRight' ? 1 : -1,
      pickSellerAndOfferingsOfferings.length,
    );
  } else if (key === 'ArrowUp') {
    isFunctionButtonFocused.value = true;
  } else if (key === 'Enter') {
    const offering = pickSellerAndOfferingsOfferings[focusedTileIndex.value];
    toggleOfferingInCart(offering.offeringUuid);
  }
};

const handleStage04Navigation = (key) => {
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    focusedButtonIndex.value = clampIndex(
      focusedButtonIndex.value,
      key === 'ArrowRight' ? 1 : -1,
      2,
    );
  } else if (key === 'Enter') {
    if (focusedButtonIndex.value === 0) {
      goToStage(5);
    } else {
      dismissOverlay();
    }
  }
};

const handleStage05Navigation = (key) => {
  if (key === 'Enter') {
    dismissOverlay();
  }
};

const handleErrorStageNavigation = (key) => {
  if (key === 'Enter') {
    dismissOverlay();
  }
};

const keyDownHandler = (e) => {
  trackKeyPress(e);

  if (isDismissed.value) return;

  if (e.key === 'Escape' || e.key === 'Backspace') {
    dismissOverlay();
    return;
  }

  if (currentStage.value === 1) {
    handleStage01Navigation(e.key);
  } else if (currentStage.value === 2) {
    handleStage02Navigation(e.key);
  } else if (currentStage.value === 3) {
    handleStage03Navigation(e.key);
  } else if (currentStage.value === 4) {
    handleStage04Navigation(e.key);
  } else if (currentStage.value === 5) {
    handleStage05Navigation(e.key);
  } else if (currentStage.value === 'error') {
    handleErrorStageNavigation(e.key);
  }
};

// // PickSellerAndOfferings watchers

watch(currentStage, (newStage) => {
  focusedButtonIndex.value = 0;
  focusedTileIndex.value = 0;
  isFunctionButtonFocused.value = false;
  startCountdown(STAGE_COUNTDOWN_SECONDS[newStage]);
});

// // PickSellerAndOfferings lifecycle hooks

onMounted(async () => {
  await waitForComponentAssets(containerRef.value);
  templateFadeInHandler();

  document.addEventListener('keydown', keyDownHandler);
});

onUnmounted(() => {
  document.removeEventListener('keydown', keyDownHandler);
});
</script>

<template>
  <section
    ref="containerRef"
    class="pick-seller-and-offerings"
    :class="{
      'is-clear': !isTemplateReadyToFadeIn || !isAssetsLoaded,
      'is-opaque': isTemplateReadyToFadeIn && isAssetsLoaded,
      'is-gone': isDismissed,
    }"
  >
    <div class="pick-seller-and-offerings__container">
      <div class="pick-seller-and-offerings__scrim"></div>

      <TvP08PopupComp
        v-if="currentStage === 1"
        class="pick-seller-and-offerings__popup"
        :popup-title="storeBreak.breakTaglineCopy"
        :popup-message="storeBreak.breakHeadlineCopy"
        :has-secondary-button="true"
        :focused-button-index="focusedButtonIndex"
        primary-button-copy="View Restaurants"
        secondary-button-copy="Dismiss"
        :is-secondary-with-progress="true"
        :secondary-progress-seconds-remaining="countdownSecondsRemaining"
        :secondary-progress-total-seconds="STAGE_COUNTDOWN_SECONDS[1]"
      >
        <template v-if="isWaitingForRecommendations" #body>
          <div class="pick-seller-and-offerings__loading-spinner"></div>
        </template>
      </TvP08PopupComp>

      <div
        v-if="currentStage === 2 || currentStage === 3"
        class="pick-seller-and-offerings__countdown"
      >
        <TvP08ProgressCircleComp
          :seconds-remaining="countdownSecondsRemaining"
          :total-seconds="STAGE_COUNTDOWN_SECONDS[currentStage]"
        />
      </div>

      <div v-if="currentStage === 2" class="pick-seller-and-offerings__tile-stage">
        <div class="pick-seller-and-offerings__heading">{{ sellerRowHeading }}</div>

        <div class="pick-seller-and-offerings__psp-line">
          GimmieNow - Delivered via
          <img
            class="pick-seller-and-offerings__psp-logo"
            :src="pickSellerAndOfferingsPsp.pspLogo"
            :alt="pickSellerAndOfferingsPsp.pspTitle"
          />
        </div>

        <div class="pick-seller-and-offerings__seller-row">
          <TvP08ContentRowComp
            :items="sellerTiles"
            :focused-index="focusedTileIndex"
            :item-width="ROW_ITEM_WIDTH"
            :item-gap="ROW_ITEM_GAP"
          >
            <template #default="{ item, isFocused }">
              <TvP08ContentTileComp
                :tile-image="item.tileImage"
                :tile-title="item.tileTitle"
                :tile-subtitle="item.tileSubtitle"
                :is-focused="isFocused"
              />
            </template>
          </TvP08ContentRowComp>
        </div>
      </div>

      <div v-if="currentStage === 3" class="pick-seller-and-offerings__function-button">
        <TvP08FunctionButtonComp :is-focused="isFunctionButtonFocused" />
      </div>

      <div v-if="currentStage === 3" class="pick-seller-and-offerings__tile-stage">
        <div class="pick-seller-and-offerings__heading">{{ selectedSeller?.name }} · Menu</div>

        <div class="pick-seller-and-offerings__psp-line">
          GimmieNow - Delivered via
          <img
            class="pick-seller-and-offerings__psp-logo"
            :src="pickSellerAndOfferingsPsp.pspLogo"
            :alt="pickSellerAndOfferingsPsp.pspTitle"
          />
        </div>

        <div class="pick-seller-and-offerings__offerings-row">
          <TvP08ContentRowComp
            :items="offeringTiles"
            :focused-index="focusedTileIndex"
            :item-width="ROW_ITEM_WIDTH"
            :item-gap="ROW_ITEM_GAP"
          >
            <template #default="{ item, isFocused }">
              <TvP08ContentTileComp
                :tile-image="item.tileImage"
                :tile-title="item.tileTitle"
                :tile-subtitle="item.tileSubtitle"
                :is-focused="isFocused && !isFunctionButtonFocused"
                :has-badge="item.hasBadge"
                badge-copy="✓ Added"
              />
            </template>
          </TvP08ContentRowComp>
        </div>
      </div>

      <TvP08PopupComp
        v-else-if="currentStage === 4"
        class="pick-seller-and-offerings__popup"
        popup-title="Confirm Your Order"
        :popup-message="checkoutMessage"
        :has-secondary-button="true"
        :focused-button-index="focusedButtonIndex"
        primary-button-copy="Order"
        secondary-button-copy="Dismiss"
        :is-secondary-with-progress="true"
        :secondary-progress-seconds-remaining="countdownSecondsRemaining"
        :secondary-progress-total-seconds="STAGE_COUNTDOWN_SECONDS[4]"
      >
        <template #body>
          <div class="pick-seller-and-offerings__receipt">
            <div
              v-for="offering in cartOfferings"
              :key="offering.offeringUuid"
              class="pick-seller-and-offerings__receipt-row"
            >
              <span>{{ offering.offeringTitle }}</span>

              <span>{{ offering.offeringPrice }}</span>
            </div>
            <div
              class="pick-seller-and-offerings__receipt-row pick-seller-and-offerings__receipt-row--spacer"
            ></div>

            <div class="pick-seller-and-offerings__receipt-row">
              <span>Tax</span>

              <span>${{ pickSellerAndOfferingsFees.taxAmount.toFixed(2) }}</span>
            </div>

            <div class="pick-seller-and-offerings__receipt-row">
              <span>Delivery Fee</span>

              <span>${{ pickSellerAndOfferingsFees.deliveryFeeAmount.toFixed(2) }}</span>
            </div>

            <div class="pick-seller-and-offerings__receipt-row">
              <span>Service Fee</span>

              <span>${{ pickSellerAndOfferingsFees.serviceFeeAmount.toFixed(2) }}</span>
            </div>
          </div>
        </template>
      </TvP08PopupComp>

      <TvP08PopupComp
        v-else-if="currentStage === 5"
        class="pick-seller-and-offerings__popup"
        popup-title="Order placed!"
        :popup-message="confirmationMessage"
        :focused-button-index="0"
        primary-button-copy="Dismiss"
        :is-primary-with-progress="true"
        :primary-progress-seconds-remaining="countdownSecondsRemaining"
        :primary-progress-total-seconds="STAGE_COUNTDOWN_SECONDS[5]"
      />

      <TvP08PopupComp
        v-if="currentStage === 'error'"
        class="pick-seller-and-offerings__popup"
        popup-title="Error"
        :popup-message="errorMessage"
        :focused-button-index="0"
        primary-button-copy="Dismiss"
        :is-primary-with-progress="true"
        :primary-progress-seconds-remaining="countdownSecondsRemaining"
        :primary-progress-total-seconds="STAGE_COUNTDOWN_SECONDS['error']"
      />

      <div class="pick-seller-and-offerings__hint-bar">
        <span v-if="currentStage === 3">↑ ↓ ← → Navigate</span>
        <span v-else-if="currentStage !== 5 && currentStage !== 'error'">← → Navigate</span>

        <span>◯ Select</span>

        <span>↩ Dismiss</span>
      </div>
    </div>
  </section>
</template>
